import { useTranslation } from '@crew/modules/i18n'
import { ValidateRules } from '@crew/utils/form'
import { ValidationStatus } from 'devextreme/common'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNewUserJoinProjectMutation } from '@crew/apis/project/projectApis'
import {
  useLazyCheckDuplicateLoginIdQuery,
  useLoginMutation,
} from '@crew/apis/app/appApis'

export type FormValues = {
  loginId: string
  fullName: string
  password: string
  passwordConfirm: string
  agree: boolean
}
const formInitialValues: FormValues = {
  loginId: '',
  fullName: '',
  password: '',
  passwordConfirm: '',
  agree: false,
}

export const useSignupNewAccount = () => {
  const { t } = useTranslation()

  const [loginIdValidationStatus, setLoginIdValidationStatus] =
    useState<ValidationStatus>('valid')

  const {
    control,
    handleSubmit,
    formState,
    getValues,
    setError,
    clearErrors,
    watch,
    trigger,
  } = useForm<FormValues>({
    defaultValues: formInitialValues,
    criteriaMode: 'all',
  })

  // Get function for join project
  const [
    newUserJoinProjectMutation,
    { isLoading: isLoadingNewUserJoinProject },
  ] = useNewUserJoinProjectMutation()
  const [loginMutation, { isLoading: isLoadingLogin }] = useLoginMutation()

  const [lazyCheckDuplicateLoginIdQuery] = useLazyCheckDuplicateLoginIdQuery()

  // Join project process
  const joinProject = useCallback(
    async (data: FormValues, invitationToken: string) => {
      const payload = {
        ...data,
        displayName: data.fullName,
        invitationToken,
      }
      await newUserJoinProjectMutation({
        joinProject: payload,
      }).unwrap()
    },
    [newUserJoinProjectMutation]
  )

  // Login process
  const login = useCallback(
    async (loginId: string, password: string) => {
      const result = await loginMutation({
        loginId: loginId,
        password: password,
      }).unwrap()

      return result
    },
    [loginMutation]
  )

  //validate confirm password equal password
  const checkPasswordConfirm = useCallback(() => {
    return getValues('password') === getValues('passwordConfirm')
  }, [getValues])

  // Check require login ID and update validation state
  const checkLoginIdEmpty = useCallback(() => {
    const loginId = getValues('loginId')
    if (loginId.length === 0) {
      setLoginIdValidationStatus('invalid')
      return false
    }
    return true
  }, [getValues])

  // 以下、入力コンポーネントのルールとレンダリングを定義
  const validateRules: ValidateRules<FormValues> = useMemo(
    () => ({
      loginId: {
        validate: {
          checkLoginIdEmpty: () =>
            checkLoginIdEmpty() ||
            t('message.general.isRequired', {
              name: t('label.loginId'),
            }),
        },
        maxLength: {
          value: 500,
          message: t('message.general.maxLength', {
            name: t('label.loginId'),
            value: 500,
          }),
        },
      },
      fullName: {
        required: t('message.general.isRequired', {
          name: t('label.displayName'),
        }),
      },
      password: {
        required: t('message.general.isRequired', {
          name: t('label.password'),
        }),
        // TODO: 暫定で6文字以上かのみチェック。恒久対応はCREW-10657で対応する。
        // https://break-tmc.atlassian.net/browse/CREW-10657
        minLength: {
          value: 6,
          message: '6文字以上で入力してください', // サインアップ画面はi18nが使えないかつ暫定実装のため、日本語で固定表示
        },
      },
      passwordConfirm: {
        required: t('message.general.isRequired', {
          name: t('label.confirmPassword'),
        }),
        validate: {
          checkPasswordConfirm: () =>
            checkPasswordConfirm() ||
            t('message.signup.register.invalidFormatConfirmPassword'),
        },
      },
      agree: {},
    }),
    [checkLoginIdEmpty, checkPasswordConfirm, t]
  )

  // Check the existence of loginId
  const verifyLoginId = useCallback(
    async (loginId: string) => {
      // ログインIDの重複がないかチェック
      const response = await lazyCheckDuplicateLoginIdQuery({
        loginId,
      }).unwrap()

      // if duplicate login ID then set error
      if (response.duplicated) {
        setLoginIdValidationStatus('invalid')
        setError('loginId', {
          type: 'manual',
          message: t('message.signup.register.loginIdAlreadyExists'),
        })
      } else {
        // if not duplicate login ID then set valid
        setLoginIdValidationStatus('valid')
        clearErrors('loginId')
      }
    },
    [clearErrors, lazyCheckDuplicateLoginIdQuery, setError, t]
  )

  return {
    control,
    handleSubmit,
    formState,
    getValues,
    setError,
    clearErrors,
    validateRules,
    watch,
    trigger,
    loginIdValidationStatus,
    setLoginIdValidationStatus,
    joinProject,
    login,

    verifyLoginId,
    isLoadingLogin,
    isLoadingNewUserJoinProject,
  }
}
